import { Trans } from "react-i18next";

import {
  ArticleBoardType,
  ArticleCategory,
  CategoryOptionList,
  ContentFormActionType,
  ContentFormInitialState,
} from "@sellernote/shared/src/api/dto/entities/common/support";

/**
 * 주어진 카테고리에 해당하는 번역된 문자열을 반환합니다.
 *
 * @param category - ArticleCategory 타입의 카테고리 값
 * @returns 주어진 카테고리에 해당하는 번역된 문자열
 */
export function getArticleCategoryString(category: ArticleCategory) {
  switch (category) {
    case "shipdaService":
      return <Trans i18nKey="support-new:공통_쉽다_서비스_소개" />;
    case "searchEstimate":
      return <Trans i18nKey="support-new:공통_실시간_견적조회_이용방법" />;
    case "afterSearchEstimate":
      return (
        <Trans i18nKey="support-new:공통_실시간_견적조회_의뢰_후_프로세스" />
      );
    case "oceanTicket":
      return <Trans i18nKey="support-new:공통_오션티켓_서비스_이용방법" />;
    case "afterOceanTicket":
      return <Trans i18nKey="support-new:공통_오션티켓_의뢰_후_프로세스" />;
    case "consolidation":
      return <Trans i18nKey="support-new:공통_창고입고_후_수입운송_이용방법" />;
    case "afterConsolidation":
      return (
        <Trans i18nKey="support-new:공통_창고입고_수입운송_의뢰_후_프로세스" />
      );
    case "boful":
      return <Trans i18nKey="support-new:공통_보풀_이용방법" />;
    case "afterBoful":
      return <Trans i18nKey="support-new:공통_보풀_의뢰_후_프로세스" />;
    case "forShipdaUser":
      return <Trans i18nKey="support-new:공통_쉽다_이용고객님들께" />;
    default:
      return category;
  }
}

/**
 * 주어진 게시판 타입에 해당하는 번역된 문자열을 반환합니다.
 *
 * @param articleType - ArticleBoardType 타입의 게시판 타입 값
 * @returns 주어진 게시판 타입에 해당하는 번역된 문자열
 */
export function getArticleBoardTypeString(articleType: ArticleBoardType) {
  switch (articleType) {
    case "guide":
      return <Trans i18nKey="support-new:공통_가이드" />;
    case "terms":
      return <Trans i18nKey="support-new:공통_무역용어_운송프로세스" />;
    case "howto":
      return <Trans i18nKey="support-new:공통_이용방법" />;
    case "faq":
      return "FAQ";
    case "notice":
      return <Trans i18nKey="support-new:공통_공지사항" />;
    case "video":
      return <Trans i18nKey="support-new:공통_소개_활용_동영상" />;
    default:
      return articleType;
  }
}

/**
 * 주어진 게시판 타입에 해당하는 URL을 반환합니다.
 *
 * @param articleType - ArticleBoardType 타입의 게시판 타입 값
 * @returns 주어진 게시판 타입에 해당하는 URL 문자열
 */
export function getBoardHref(articleType: ArticleBoardType) {
  switch (articleType) {
    case "guide":
      return "/support/guide";
    case "terms":
      return "/support/guide/tradeterms";
    case "howto":
      return "/support/guide/use";
    case "faq":
      return "/support/guide/faq";
    case "notice":
      return "/support/notice";
    case "video":
      return "/support/onlinelecture";
    default:
      return articleType;
  }
}

/**
 * 주어진 카테고리 옵션에 해당하는 번역된 문자열을 반환합니다.
 *
 * @param category - CategoryOptionList 타입의 카테고리 옵션 값
 * @returns 주어진 카테고리 옵션에 해당하는 번역된 문자열
 */
export function getSelectArticleCategoryString(category: CategoryOptionList) {
  switch (category) {
    case "shipdaService":
      return <Trans i18nKey="support-new:공통_쉽다_서비스_소개" />;
    case "searchEstimate":
      return <Trans i18nKey="support-new:공통_실시간_견적조회_이용방법" />;
    case "afterSearchEstimate":
      return (
        <Trans i18nKey="support-new:공통_실시간_견적조회_의뢰_후_프로세스" />
      );
    case "oceanTicket":
      return <Trans i18nKey="support-new:공통_오션티켓_서비스_이용방법" />;
    case "afterOceanTicket":
      return <Trans i18nKey="support-new:공통_오션티켓_의뢰_후_프로세스" />;
    case "consolidation":
      return <Trans i18nKey="support-new:공통_창고입고_후_수입운송_이용방법" />;
    case "afterConsolidation":
      return (
        <Trans i18nKey="support-new:공통_창고입고_수입운송_의뢰_후_프로세스" />
      );
    case "boful":
      return <Trans i18nKey="support-new:공통_보풀_이용방법" />;
    case "afterBoful":
      return <Trans i18nKey="support-new:공통_보풀_의뢰_후_프로세스" />;
    case "forShipdaUser":
      return <Trans i18nKey="support-new:공통_쉽다_유저에게" />;
    default:
      return "";
  }
}

interface ValueObject {
  articleType: string;
  contents: string;
  title: string;
  videoURL: string;
  categoryValue: string | undefined;
}

/**
 * 주어진 값 객체를 기반으로 제출 버튼이 비활성화되어야 하는지 여부를 반환합니다.
 *
 * @param valueObject - ValueObject 타입의 값 객체
 * @returns 제출 버튼이 비활성화되어야 하는지 여부를 나타내는 boolean 값
 */
export function checkContentFormSubmitButtonDisabled(valueObject: ValueObject) {
  if (valueObject.articleType === "video") {
    if (
      valueObject.contents === "" ||
      valueObject.title === "" ||
      valueObject.videoURL === "" ||
      valueObject.contents === "<p><br></p>" ||
      valueObject.categoryValue === undefined
    ) {
      return true;
    }
    return false;
  } else {
    if (
      valueObject.contents === "" ||
      valueObject.title === "" ||
      valueObject.contents === "<p><br></p>"
    ) {
      return true;
    }
    return false;
  }
}

/**
 * 주어진 상태와 액션을 기반으로 새로운 상태를 반환하는 리듀서 함수입니다.
 *
 * @param state - ContentFormInitialState 타입의 현재 상태
 * @param action - ContentFormActionType 타입의 액션 객체
 * @returns 새로운 상태를 나타내는 ContentFormInitialState 객체
 */
export function contentFormReducer(
  state: ContentFormInitialState,
  action: ContentFormActionType
): ContentFormInitialState {
  switch (action.type) {
    case "CHANGE_CONTENTS":
      return {
        ...state,
        contents: action.value,
      };
    case "CHANGE_TITLE":
      return {
        ...state,
        title: action.value,
      };
    case "CHANGE_MEMO":
      return {
        ...state,
        memo: action.value,
      };
    case "CHANGE_VIDEO_URL":
      return {
        ...state,
        videoURL: action.value,
      };
    case "CHANGE_CATEGORY":
      return {
        ...state,
        category: {
          label: action.label,
          value: action.value,
        },
      };
    default:
      return state;
  }
}
